<template>
	<div
		class="cog-checkable cog-input"
		:class="[
			allowOther && 'cog-checkable--allow-other',
			hasColumns ? columnClasses : 'cog-checkable--inline',
			readonly && 'is-read-only',
			isCard && 'cog-checkable--card',
			isCard && 'cog-checkable--label-on-' + (!hasAtLeastOneImage || hideChoiceLabels ? 'bottom' : cardLabelPosition),
			hasAtLeastOneImage && choiceImageSizeClass,
			isPersonField && hasAtLeastOneImage && 'cog-checkable--avatars'
		]"
	>
		<div
			v-if="readonly" v-show="displayValue"
			:class="{
				'cog-checkable__item--no-label': hideChoiceLabels && selectedValue && hasImage(selectedValue)
			}"
			class="cog-checkable__item is-read-only"
		>
			<div class="cog-choice-option">
				<CChoiceImage
					v-if="selectedValue && hasImage(selectedValue)"
					:option="selectedValue"
					v-bind="{isPersonField, choiceImageSize, fontSize}"
					@dimensions="processDimensions"
				/>
				<!-- Lack of whitespace for multi-line calculations -->
				<div v-if="!hideChoiceLabels || !hasImage(selectedValue)" class="cog-choice__readonly-label"><span class="cog-choice__value">{{ selectedValue && selectedValue.label ? selectedValue.label : displayValue | breakWords | spaceIfEmpty }}</span><span v-if="isCard && selectedValue.description" class="cog-choice__description">{{ selectedValue.description }}</span></div>
			</div>
		</div>
		<el-radio-group
			v-else
			v-model="internalValue"
			:role="null"
			:label="null"
			@change="handleChange"
		>
			<el-radio
				v-for="(option, i) in options"
				:key="option.value"
				:label="option.value"
				:value="option.value"
				class="cog-checkable__item cog-choice-option"
				:class="[
					{'cog-inner-col__item': hasColumns},
					{'cog-checkable__item--no-label': hideChoiceLabels && hasImage(option)}
				]"
				:style="`order: ${optionOrder[i]}`"
				@keydown.space.native="handleRadioSpace"
				@click.native="$emit('focus-lost')"
			>
				<template v-if="isCard">
					<ICardCheck class="cog-choice__card-checkmark" />
					<CChoiceImage
						v-if="hasImage(option)"
						v-bind="{option, isPersonField, choiceImageSize, fontSize}"
						@dimensions="processDimensions"
					/>
					<div v-if="!(hideChoiceLabels && hasImage(option))">
						<span class="cog-choice__value">{{ option.label ? option.label : option.displayValue | breakWords }}</span>
						<span v-if="option.description" class="cog-choice__description">{{ cleanChoiceDescription(option.description) }}</span>
					</div>
				</template>
				<template v-if="!isCard && hasImage(option)" #image>
					<CChoiceImage v-bind="{option, isPersonField, choiceImageSize, fontSize}" />
				</template>
				<template v-if="!isCard && !(hideChoiceLabels && hasImage(option))">
					<span class="cog-choice__value">{{ option.label ? option.label : option.displayValue | breakWords }}</span>
					<span v-if="option.description" class="cog-choice__description">{{ cleanChoiceDescription(option.description) }}</span>
				</template>
			</el-radio>
			<el-radio
				v-if="allowOther"
				ref="otherRadio"
				:style="`order: ${optionOrder[options.length]}`"
				:class="hasColumns ? 'cog-inner-col__item' : null"
				class="cog-checkable__item cog-choice-option cog-checkable__allow-other"
				:label="otherValue"
				@change="handleOtherRadioChange"
				@click.native="handleOtherRadioClick"
			>
				<el-input
					ref="otherInput"
					v-model="otherValue"
					:tabindex="otherIsSelected ? '0' : '-1'"
					:placeholder="$resource('field-choice-other-fill-in')"
					@focus="handleOtherFocus"
					@blur="handleOtherBlur"
					@input="handleOtherInput"
				/>
			</el-radio>
		</el-radio-group>
	</div>
</template>

<script>
	import '../style/_checkables.scss';
	import '../style/_checkable-layout.scss';
	import '../style/_checkable-cards.scss';

	import Vue from 'vue';
	import ICardCheck from 'src/assets/card-check.svg';
	import { RadioGroup, Radio, Input } from '@cognitoforms/element-ui';
	import columnizedOptions from 'src/mixins/columnized-options';
	import CChoiceImage from './ChoiceImage.vue';
	import resetComponentData from 'src/mixins/resetComponentData';
	import { getChoiceImageSizeClass } from './use-choice-image-size';
	import { cleanChoiceDescription, alignChoiceItems, constrainCardDimensions } from './choiceUtils';

	Vue.use(RadioGroup);
	Vue.use(Radio);
	Vue.use(Input);

	export default Vue.extend({
		name: 'CRadioGroup',
		components: { CChoiceImage, ICardCheck },
		filters: {
			spaceIfEmpty(value) {
				// https://stackoverflow.com/a/19810263/2836695
				return value || '\xa0';
			}
		},
		mixins: [columnizedOptions, resetComponentData],
		inject: ['$resource'],
		inheritAttrs: false,
		model: {
			prop: 'value',
			event: 'change'
		},
		props: {
			value: { type: String, default: null },
			readonly: Boolean,
			allowOther: Boolean,
			choiceImageSize: { type: Number, default: 0 },
			isCard: { type: Boolean, default: false },
			showChoiceImages: { type: Boolean, default: false },
			hideChoiceLabels: { type: Boolean, default: false },
			isPersonField: { type: Boolean, default: false },
			cardLabelPosition: { type: String, default: 'bottom' },
			imagesInChoiceFieldsEnabled: { type: Boolean, default: false }
		},
		data() {
			return {
				internalValue: this.value,
				// The other value being undefined keeps it from being selected
				// (the radio is selected if its label is the same as the group's value, `internalValue`, which is blank string)
				otherValue: this.isOtherValue(this.value) ? this.value : undefined,
				resizeObserver: null,
				fontSize: undefined,
				dimensionConstraints: null,
				lookupDimensions: []
			};
		},
		computed: {
			selectedValue() {
				return this.options.find(o => o.value === this.value);
			},
			displayValue() {
				if (this.value) {
					if (this.selectedValue)
						return this.selectedValue.displayValue;
					else if (this.allowOther)
						return this.value;
				}
				return '';
			},
			otherIsSelected() {
				return this.internalValue === this.otherValue;
			},
			choiceImageSizeClass() {
				return getChoiceImageSizeClass(this.choiceImageSize);
			},
			hasAtLeastOneImage() {
				return this.options.some(item => this.hasImage(item));
			}
		},
		watch: {
			value: {
				immediate: true,
				handler(value) {
					this.internalValue = value;
					if (this.isOtherValue(value))
						this.otherValue = value;
				}
			},
			internalValue: {
				immediate: false,
				async handler(value) {
					await this.$nextTick();
					this.setTabIndex();
				}
			},
			dimensionConstraints(constraints) {
				if (constraints) {
					const imageContainers = this.$el.querySelectorAll('.cog-choice-image-container');
					imageContainers.forEach((container) => {
						if (constraints?.maxHeight)
							container.style.maxHeight = constraints.maxHeight;
						else if (constraints?.maxWidth)
							container.style.maxWidth = constraints.maxWidth;
					});
				}
			}
		},
		created () {
			this.changeHandlerName = ['handleChange', 'handleOtherBlur'];
		},
		mounted() {
			this.fontSize = parseInt(getComputedStyle(this.$el).fontSize);

			if (this.isCard && this.options.some(opt => opt.choiceImageDimensions?.width && opt.choiceImageDimensions?.height))
				this.dimensionConstraints = constrainCardDimensions(this.options);

			this.resizeObserver = new ResizeObserver(() => {
				alignChoiceItems(this.$el);
			});
			this.resizeObserver.observe(this.$el);
		},
		beforeDestroy() {
			if (this.resizeObserver)
				this.resizeObserver.disconnect();
		},
		methods: {
			hasImage(item) {
				return item.svg || item.thumbnailImage || item.choiceThumbnailImage;
			},
			isOtherValue(value) {
				return !!value && this.allowOther && !this.options.map(o => o.value).includes(value);
			},
			handleOtherFocus() {
				this.updating = true;

				// If the other option is undefined when focussed, then set it to blank string to cause it to be selected
				if (!this.otherValue)
					this.otherValue = '';

				// Treat focus on the "Other" textbox as its first input event (which will seed the internal value with blank string)
				this.handleOtherInput();

				if (this.internalValue !== this.value) {
					// Emit change when "Other" is focused if there is a value, since the blank other value is now selected
					this.$emit('change', this.internalValue);
				}
			},
			handleOtherInput() {
				// Change the internal value to match the other value when it is selected or changed
				this.internalValue = this.otherValue;

				this.$emit('input', this.internalValue);
			},
			handleOtherBlur(ev) {
				// If the other value is blank when blurred, then set the value to `undefined` to cause it to be de-selected
				if (!this.otherValue)
					this.otherValue = undefined;

				// Raise the change event if the value has actually changed (i.e. the user didn't simply focus into the input then leave)
				if (this.internalValue !== this.value) {
					this.$emit('change', this.internalValue);
				}
				this.setTabIndex();
			},
			async handleOtherRadioChange(e) {
				await this.$nextTick();
				this.$refs.otherInput.focus();
			},
			async handleOtherRadioClick(e) {
				await this.$nextTick();
				this.$refs.otherInput.focus();
			},
			handleChange(value) {
				this.internalValue = value;
				this.$emit('change', this.internalValue);
				this.setTabIndex();
			},
			handleRadioSpace() {
				this.$emit('change', this.internalValue);
			},
			setTabIndex() {
				const firstRadio = this.$el.querySelector('.el-radio input');

				if (!firstRadio)
					return;

				// If the first radio is checked, tabbing order will be correct.
				if (firstRadio.getAttribute('aria-checked') === 'true') {
					firstRadio.setAttribute('tabindex', '0');
					return;
				}

				setTimeout(()=> {
					const checked = this.$el.querySelector('[aria-checked="true"]');

					// If nothing is checked, put tabindex=0 on the first radio. Element does not account for deselecting radio groups.
					// Otherwise, if a radio other than the first is checked, take the first radio out of tabbing order. This fixes a bug in Element.
					if (!checked)
						firstRadio.setAttribute('tabindex', '0');
					else
						firstRadio.setAttribute('tabindex', '-1');
				});
			},
			cleanChoiceDescription,
			processDimensions(dimensions) {
				this.lookupDimensions.push(dimensions);
				if (this.isCard && this.lookupDimensions.length === this.options.filter(o => this.hasImage(o)).length)
					this.dimensionConstraints = constrainCardDimensions(this.lookupDimensions);
			}
		}
	});
</script>
